import React, { useEffect, useMemo, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import client from '../../api/client';
import {
  Globe, Eye, Link as LinkIcon, Save, Loader2, Check, ExternalLink,
  Plus, Trash2, Copy, EyeOff, Sparkles, Heart, BarChart3, ShieldCheck,
  Music2, Volume2, Upload as UploadIcon, X,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import PhotoUploader from '../../components/PhotoUploader';
import InfoTip from '../../components/InfoTip';
import AiAssistButton from '../../components/AiAssistButton';
import CaptionEditor from '../../components/CaptionEditor';

const TEMPLATES = [
  { id: 'minimal',  name: 'Minimal',  blurb: 'Quiet, modern, mostly white.' },
  { id: 'rustic',   name: 'Rustic',   blurb: 'Warm earth tones, cozy serif.' },
  { id: 'classic',  name: 'Classic',  blurb: 'Romantic, our house style.' },
  { id: 'boho',     name: 'Boho',     blurb: 'Soft camel, dreamy.' },
  { id: 'garden',   name: 'Garden',   blurb: 'Greens, fresh & airy.' },
  { id: 'coastal',  name: 'Coastal',  blurb: 'Misty blues, breezy.' },
];

export default function WeddingWebsite() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const [{ data: c }, { data: a }] = await Promise.all([
          client.get('/website/config'),
          client.get('/website/analytics').catch(() => ({ data: null })),
        ]);
        setCfg(c);
        setAnalytics(a);
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const save = async (patch) => {
    setCfg((prev) => ({ ...prev, ...patch }));
    setSaving(true);
    try {
      const { data } = await client.put('/website/config', patch);
      setCfg(data);
      setSavedAt(Date.now());
    } catch (e) {
      toast({ title: "Couldn't save", description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = useMemo(() => {
    if (!cfg?.slug) return '';
    return `${window.location.origin}/wedding/${cfg.slug}`;
  }, [cfg?.slug]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast({ title: 'Link copied' });
    } catch (e) {
      toast({ title: 'Copy failed', description: 'Long-press the link to copy.' });
    }
  };

  if (loading || !cfg) {
    return <div className="flex items-center gap-2 text-[#6A6A6A]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="animate-float-up">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
        <div>
          <p className="section-eyebrow">Your wedding website</p>
          <h1 className="font-serif text-4xl text-[#2A2A2A] mt-2 flex items-center gap-3"><AppIcon feature="website" size="lg" reveal testId="section-icon-website" />Share the day, your way.</h1>
          <p className="text-sm text-[#6A6A6A] mt-1">Auto-built from your dashboard — toggle on anything you want to share.</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            data-testid="website-preview-link"
            href={`/wedding/${cfg.slug}?preview=1`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-white border border-[#EFE3D8] text-[#2A2A2A] hover:border-[#7B2C3B]"
          >
            <Eye className="w-4 h-4" /> Preview
          </a>
          <button
            data-testid="website-publish-toggle"
            onClick={() => save({ published: !cfg.published })}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium ${cfg.published ? 'bg-[#7B2C3B] text-[#FBF7F2]' : 'bg-[#F4E4DA] text-[#7B2C3B] border border-[#EBD3C2]'}`}
          >
            {cfg.published ? <><Globe className="w-4 h-4" /> Published</> : <><EyeOff className="w-4 h-4" /> Private</>}
          </button>
        </div>
      </div>

      {/* Share link strip */}
      <div className="soft-card rounded-2xl p-4 mb-6 bg-white border border-[#EFE3D8]">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-[#6A6A6A]">
            <LinkIcon className="w-4 h-4 text-[#7B2C3B]" />
            <span>Public link</span>
          </div>
          <code data-testid="website-public-url" className="flex-1 text-sm text-[#2A2A2A] bg-[#FBF7F2] px-3 py-2 rounded-lg border border-[#EFE3D8] truncate">{publicUrl}</code>
          <button onClick={copyLink} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2]">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#7B2C3B] hover:underline">
            Open <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className="text-[#6A6A6A] inline-flex items-center gap-1">
            Slug:
            <InfoTip testId="info-slug" title="Your unique site address">
              <p>The slug is the last part of your public wedding-site URL, e.g. <code>/wedding/<strong>maya-and-jordan</strong></code>.</p>
              <p>Keep it short and easy to type — lowercase letters, numbers and dashes only. If someone else has already claimed it, we&apos;ll automatically add a number to make yours unique.</p>
              <p>Once you share the link with guests, avoid changing it — the old address will stop working.</p>
            </InfoTip>
          </span>
          <input
            data-testid="website-slug-input"
            value={cfg.slug || ''}
            onChange={(e) => setCfg((p) => ({ ...p, slug: e.target.value }))}
            onBlur={(e) => e.target.value !== cfg.slug && save({ slug: e.target.value })}
            className="px-2 py-1 rounded-md border border-[#EFE3D8] bg-white text-xs"
          />
          {saving && <span className="text-[#9A8A7E] inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> saving</span>}
          {!saving && savedAt && <span className="text-emerald-600 inline-flex items-center gap-1"><Check className="w-3 h-3" /> saved</span>}
        </div>
      </div>

      {/* Editor grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LEFT: editing controls */}
        <div className="lg:col-span-2 space-y-5">
          {/* Analytics card */}
          {cfg.published && analytics && (
            <section data-testid="website-analytics" className="soft-card rounded-2xl p-5 bg-gradient-to-br from-[#F4E4DA] to-[#FBF7F2] border border-[#EFE3D8]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#7B2C3B]" /> Visitors this week
                </h3>
                <span className="inline-flex items-center gap-1 text-[10px] text-[#6A6A6A] bg-white/70 px-2 py-0.5 rounded-full border border-[#EFE3D8]">
                  <ShieldCheck className="w-3 h-3 text-[#7B2C3B]" /> Aggregate only · no tracking cookies
                </span>
              </div>
              <div className="flex items-end gap-1.5 h-24">
                {analytics.series.map((d, i) => {
                  const max = Math.max(1, ...analytics.series.map((s) => s.visits));
                  const h = Math.max(4, Math.round((d.visits / max) * 100));
                  return (
                    <div key={i} data-testid={`analytics-bar-${i}`} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full rounded-md bg-[#7B2C3B]" style={{ height: `${h}%` }} title={`${d.visits} on ${d.day}`} />
                      <span className="text-[10px] text-[#9A8A7E]">{d.day.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <div>
                  <p className="text-[#6A6A6A]">This week</p>
                  <p className="font-serif text-xl text-[#2A2A2A]">{analytics.weekTotal}</p>
                </div>
                <div className="text-right">
                  <p className="text-[#6A6A6A]">All time</p>
                  <p className="font-serif text-xl text-[#2A2A2A]">{analytics.total}</p>
                </div>
              </div>
            </section>
          )}

          {/* Template */}
          <section className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
            <h3 className="font-serif text-xl text-[#2A2A2A] mb-3 inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#7B2C3B]" /> Template
              <InfoTip testId="info-template" title="Pick a look for your site">
                <p>The template controls colours, fonts and the overall vibe of your public wedding site. Everything else (photos, text, schedule) stays the same as you switch.</p>
                <p>Not sure? <strong>Classic</strong> is our house style — romantic and warm. You can change it any time before the wedding.</p>
              </InfoTip>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {TEMPLATES.map((t) => {
                const active = cfg.template === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`website-template-${t.id}`}
                    onClick={() => save({ template: t.id })}
                    className={`text-left p-3 rounded-xl border transition-all ${active ? 'border-[#7B2C3B] bg-[#F4E4DA]' : 'border-[#EFE3D8] hover:border-[#B8896C] bg-white'}`}
                  >
                    <p className="font-serif text-base text-[#2A2A2A]">{t.name}</p>
                    <p className="text-[11px] text-[#6A6A6A] mt-0.5">{t.blurb}</p>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Hero */}
          <section className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
            <h3 className="font-serif text-xl text-[#2A2A2A] mb-3 inline-flex items-center gap-2">
              Hero
              <InfoTip testId="info-hero" title="The first thing guests see">
                <p>The hero is the big banner at the top of your site. It shows your <strong>Headline</strong>, wedding date, and venue.</p>
                <p><strong>Hero photo</strong>: a wide landscape shot used as a full-bleed background. Something scenic works best (venue, engagement location, sunset). Text sits over it, so avoid busy centers.</p>
                <p><strong>Email photo</strong>: a portrait of the two of you that shows at the top of every invitation email.</p>
                <p><strong>Our-story photo</strong>: the portrait shown next to your Our Story text on the public wedding site. Falls back to the email photo when blank.</p>
              </InfoTip>
            </h3>
            <label className="text-xs text-[#6A6A6A]">Headline</label>
            <input
              data-testid="website-headline"
              value={cfg.coverHeadline || ''}
              onChange={(e) => setCfg((p) => ({ ...p, coverHeadline: e.target.value }))}
              onBlur={(e) => save({ coverHeadline: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
              placeholder="Maya & Jordan"
            />
            <div className="mt-4">
              <PhotoUploader
                testId="website-hero"
                label="Hero photo (background of the site)"
                aspect="wide"
                value={cfg.heroPhoto || ''}
                onChange={(url) => save({ heroPhoto: url })}
              />
              {cfg.heroPhoto && (
                <CaptionEditor
                  testId="hero-caption-editor"
                  photoUrl={cfg.heroPhoto}
                  aspect="wide"
                  caption={cfg.heroCaption}
                  onChange={(next) => save({ heroCaption: next })}
                />
              )}
            </div>
            <div className="mt-4">
              <PhotoUploader
                testId="website-couple"
                label="Email invitation photo (shown at the top of every email invite)"
                aspect="portrait"
                value={cfg.couplePhoto || ''}
                onChange={(url) => save({ couplePhoto: url })}
              />
              {cfg.couplePhoto && (
                <CaptionEditor
                  testId="couple-caption-editor"
                  photoUrl={cfg.couplePhoto}
                  aspect="portrait"
                  caption={cfg.coupleCaption}
                  onChange={(next) => save({ coupleCaption: next })}
                />
              )}
            </div>
            <div className="mt-4">
              <PhotoUploader
                testId="website-story"
                label="Our Story photo (shown next to the Our Story section on your public site)"
                aspect="portrait"
                value={cfg.storyPhoto || ''}
                onChange={(url) => save({ storyPhoto: url })}
              />
              <p className="text-[10px] text-[#9A8A7E] mt-1">Leave blank to reuse the email photo above.</p>
              {(cfg.storyPhoto || cfg.couplePhoto) && (
                <CaptionEditor
                  testId="story-caption-editor"
                  photoUrl={cfg.storyPhoto || cfg.couplePhoto}
                  aspect="portrait"
                  caption={cfg.storyCaption}
                  onChange={(next) => save({ storyCaption: next })}
                />
              )}
            </div>
          </section>

          {/* Background music */}
          <MusicSection cfg={cfg} save={save} setCfg={setCfg} />


          {/* Sections toggles */}
          <section className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
            <h3 className="font-serif text-xl text-[#2A2A2A] mb-3 inline-flex items-center gap-2">
              Sections
              <InfoTip testId="info-sections" title="Show or hide sections">
                <p>Toggle which sections appear on your public site. Editors for hidden sections still work — flip them back on any time.</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li><strong>Story</strong>: how you met, in your own words</li>
                  <li><strong>Schedule</strong>: ceremony, cocktails, dinner, dancing timeline</li>
                  <li><strong>Travel</strong>: hotels, airport, parking tips</li>
                  <li><strong>Registry</strong>: links to your registries or cash funds</li>
                  <li><strong>RSVP</strong>: a call-to-reply block</li>
                  <li><strong>FAQ</strong>: common questions guests ask</li>
                  <li><strong>Gallery</strong>: extra photos of the two of you</li>
                </ul>
              </InfoTip>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(cfg.sections || {}).map(([key, val]) => (
                <label
                  key={key}
                  className={`flex items-center justify-between px-3 py-2 rounded-xl border cursor-pointer text-sm capitalize ${val ? 'bg-[#F4E4DA] border-[#EBD3C2] text-[#7B2C3B]' : 'border-[#EFE3D8] text-[#2A2A2A]'}`}
                >
                  <span>{key}</span>
                  <input
                    type="checkbox"
                    data-testid={`website-section-${key}`}
                    checked={val}
                    onChange={(e) => save({ sections: { ...cfg.sections, [key]: e.target.checked } })}
                    className="accent-[#7B2C3B]"
                  />
                </label>
              ))}
            </div>
          </section>

          {/* Story */}
          {cfg.sections?.story && (
            <section className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                  Our story
                  <InfoTip testId="info-story" title="How you met, in your own words">
                    <p>A short paragraph (3–8 sentences) about your relationship. Guests love the personal touch. This shows in the "Our story" section of your site, next to your couple photo.</p>
                    <p><strong>Prompts if you're stuck</strong>:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li>Where and when you first met</li>
                      <li>Your first date or an early inside joke</li>
                      <li>What you love most about each other</li>
                      <li>The proposal (or the shared decision to get married)</li>
                    </ul>
                    <p>Keep it in your voice — funny, sentimental, honest. Ask the <strong>AI Friend</strong> in the corner for help if you'd like a draft to react to.</p>
                  </InfoTip>
                </h3>
                <AiAssistButton
                  feature="story"
                  testId="story-ai-draft"
                  label={cfg.storyText ? 'Rewrite with AI' : 'Draft with AI'}
                  context={{ tone: 'warm', howMet: cfg.storyText || '' }}
                  onResult={(data) => {
                    if (!data?.text) return;
                    setCfg((p) => ({ ...p, storyText: data.text }));
                    save({ storyText: data.text });
                  }}
                />
              </div>
              <textarea
                data-testid="website-story"
                rows={5}
                value={cfg.storyText || ''}
                onChange={(e) => setCfg((p) => ({ ...p, storyText: e.target.value }))}
                onBlur={(e) => save({ storyText: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
              />
            </section>
          )}

          {/* Schedule */}
          {cfg.sections?.schedule && (
            <ScheduleEditor schedule={cfg.schedule} onChange={(s) => save({ schedule: s })} />
          )}

          {/* Travel */}
          {cfg.sections?.travel && (
            <section className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                  Travel &amp; stay
                  <InfoTip testId="info-travel" title="Help out-of-town guests plan the trip">
                    <p>A short note about travel logistics. Guests will see this alongside your hotel + rideshare suggestions from the <strong>Hotels</strong> tab.</p>
                    <p><strong>What to include</strong>:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li>Nearest airport(s) and typical flight time</li>
                      <li>Hotel block name + booking code (if you set one up)</li>
                      <li>Parking or shuttle details</li>
                      <li>Anything scenic to do while they're in town</li>
                    </ul>
                  </InfoTip>
                </h3>
                <AiAssistButton
                  feature="travel"
                  testId="travel-ai-draft"
                  label={cfg.travel ? 'Rewrite with AI' : 'Draft with AI'}
                  context={{ extras: cfg.travel || '' }}
                  onResult={(data) => {
                    if (!data?.text) return;
                    setCfg((p) => ({ ...p, travel: data.text }));
                    save({ travel: data.text });
                  }}
                />
              </div>
              <textarea
                data-testid="website-travel"
                rows={3}
                value={cfg.travel || ''}
                onChange={(e) => setCfg((p) => ({ ...p, travel: e.target.value }))}
                onBlur={(e) => save({ travel: e.target.value })}
                placeholder="Nearest airport, hotel block details, parking tips…"
                className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
              />
            </section>
          )}

          {/* Registry */}
          {cfg.sections?.registry && (
            <RegistryEditor links={cfg.registryLinks || []} onChange={(l) => save({ registryLinks: l })} />
          )}

          {/* FAQ */}
          {cfg.sections?.faq && (
            <FaqEditor faq={cfg.faq || []} onChange={(f) => save({ faq: f })} />
          )}
        </div>

        {/* RIGHT: live preview */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <p className="text-xs text-[#6A6A6A] mb-2 inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Live preview</p>
            <div className="rounded-2xl border border-[#EFE3D8] bg-white overflow-hidden shadow-sm" data-testid="website-live-preview">
              <MiniPreview cfg={cfg} />
            </div>
            {!cfg.published && (
              <p className="text-[11px] text-[#9A8A7E] mt-2 inline-flex items-center gap-1">
                <EyeOff className="w-3 h-3" /> Site is private — only you can see it until you publish.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// Background music editor — supports either an MP3 upload OR a
// streaming link (Spotify / YouTube / Apple Music / Pandora). Lets the
// couple set a song title + artist name.
function MusicSection({ cfg, save, setCfg }) {
  const [uploading, setUploading] = React.useState(false);
  const [err, setErr] = React.useState(null);
  // Tab: 'upload' if current URL is a file OR blank, 'link' if streaming.
  const initialTab = cfg.musicUrl && !cfg.musicUrl.startsWith('/api/files/') ? 'link' : 'upload';
  const [tab, setTab] = React.useState(initialTab);
  const [linkDraft, setLinkDraft] = React.useState(
    cfg.musicUrl && !cfg.musicUrl.startsWith('/api/files/') ? cfg.musicUrl : ''
  );
  const [linkErr, setLinkErr] = React.useState(null);

  const pickFile = () => document.getElementById('music-file-input').click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow same-file re-selection
    if (!file) return;
    setErr(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await client.post('/uploads/audio', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = data.url;
      const title = cfg.musicTitle || file.name.replace(/\.[^.]+$/, '');
      setCfg((p) => ({ ...p, musicUrl: url, musicTitle: title }));
      save({ musicUrl: url, musicTitle: title });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    setCfg((p) => ({ ...p, musicUrl: '', musicTitle: '', musicArtist: '' }));
    save({ musicUrl: '', musicTitle: '', musicArtist: '' });
    setLinkDraft('');
    setLinkErr(null);
  };

  const isStreamingHost = (u) => {
    try {
      const h = new URL(u).hostname.toLowerCase();
      return ['spotify.com', 'open.spotify.com', 'youtube.com', 'www.youtube.com', 'music.youtube.com', 'youtu.be', 'music.apple.com', 'apple.com', 'pandora.com', 'www.pandora.com', 'soundcloud.com', 'www.soundcloud.com'].some((d) => h === d || h.endsWith('.' + d));
    } catch { return false; }
  };

  const saveLink = async () => {
    const v = (linkDraft || '').trim();
    setLinkErr(null);
    if (!v) { clear(); return; }
    if (!/^https?:\/\//i.test(v) || !isStreamingHost(v)) {
      setLinkErr('Paste a Spotify, YouTube, Apple Music, Pandora or SoundCloud link.');
      return;
    }
    try {
      await save({ musicUrl: v });
    } catch (e) {
      setLinkErr(e?.response?.data?.detail || e.message || 'Save failed');
    }
  };

  const audioSrc = cfg.musicUrl && cfg.musicUrl.startsWith('/api/files/')
    ? `${process.env.REACT_APP_BACKEND_URL}${cfg.musicUrl}`
    : '';

  const providerLabel = (u) => {
    if (!u) return '';
    const ul = u.toLowerCase();
    if (ul.includes('spotify.com')) return 'Spotify';
    if (ul.includes('youtube.com') || ul.includes('youtu.be')) return 'YouTube';
    if (ul.includes('apple.com')) return 'Apple Music';
    if (ul.includes('pandora.com')) return 'Pandora';
    if (ul.includes('soundcloud.com')) return 'SoundCloud';
    return 'Streaming';
  };

  const isLink = cfg.musicUrl && !cfg.musicUrl.startsWith('/api/files/');
  const isUpload = cfg.musicUrl && cfg.musicUrl.startsWith('/api/files/');

  return (
    <section data-testid="website-music-section" className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
      <h3 className="font-serif text-xl text-[#2A2A2A] mb-3 inline-flex items-center gap-2">
        <Music2 className="w-4 h-4 text-[#7B2C3B]" /> Background music
        <InfoTip testId="info-music" title="Play music on your wedding site">
          <p>Add a song that starts playing when guests open your public wedding site. You can either upload an <strong>MP3</strong> or paste a <strong>streaming link</strong> (Spotify, YouTube, Apple Music, Pandora, SoundCloud). Playlists are supported through Spotify or YouTube.</p>
          <p>The song plays <strong>automatically</strong> when a visitor opens the site. Because modern browsers block sound-on-by-default, we start it muted and show a floating "Play music" pill — guests tap once to hear it. They can also mute or stop it any time.</p>
          <p>Uploads: MP3, M4A, AAC, OGG, WAV. Max 15 MB.</p>
          <p><em>Make sure you have rights to any song you upload.</em></p>
        </InfoTip>
      </h3>

      {/* Tabs */}
      <div className="inline-flex rounded-full border border-[#EFE3D8] p-0.5 bg-[#FBF7F2] mb-4">
        <button
          type="button"
          data-testid="music-tab-upload"
          onClick={() => setTab('upload')}
          className={`text-xs px-3 py-1.5 rounded-full transition-colors ${tab === 'upload' ? 'bg-[#7B2C3B] text-[#FBF7F2]' : 'text-[#3A3A3A]'}`}
        >
          Upload MP3
        </button>
        <button
          type="button"
          data-testid="music-tab-link"
          onClick={() => setTab('link')}
          className={`text-xs px-3 py-1.5 rounded-full transition-colors ${tab === 'link' ? 'bg-[#7B2C3B] text-[#FBF7F2]' : 'text-[#3A3A3A]'}`}
        >
          Streaming link
        </button>
      </div>

      {tab === 'upload' && (
        isUpload ? (
          <div>
            <div className="flex items-center gap-3 p-3 rounded-xl border border-[#EFE3D8] bg-[#FBF7F2]">
              <div className="w-9 h-9 rounded-full bg-[#7B2C3B] text-white flex items-center justify-center shrink-0">
                <Volume2 className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0 space-y-1.5">
                <input
                  data-testid="website-music-title"
                  value={cfg.musicTitle || ''}
                  onChange={(e) => setCfg((p) => ({ ...p, musicTitle: e.target.value }))}
                  onBlur={(e) => save({ musicTitle: e.target.value })}
                  placeholder="Song title (e.g. Perfect)"
                  className="w-full px-2 py-1 rounded border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
                />
                <input
                  data-testid="website-music-artist"
                  value={cfg.musicArtist || ''}
                  onChange={(e) => setCfg((p) => ({ ...p, musicArtist: e.target.value }))}
                  onBlur={(e) => save({ musicArtist: e.target.value })}
                  placeholder="Artist (e.g. Ed Sheeran)"
                  className="w-full px-2 py-1 rounded border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
                />
                <audio
                  data-testid="website-music-preview"
                  src={audioSrc}
                  controls
                  preload="metadata"
                  className="w-full h-8"
                />
              </div>
              <button
                type="button"
                data-testid="website-music-remove"
                onClick={clear}
                className="p-2 rounded-full text-[#B8896C] hover:text-red-600 hover:bg-red-50"
                title="Remove music"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <button
              type="button"
              data-testid="website-music-replace"
              onClick={pickFile}
              disabled={uploading}
              className="mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] disabled:opacity-60"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadIcon className="w-3.5 h-3.5" />}
              Replace song
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid="website-music-upload"
            onClick={pickFile}
            disabled={uploading}
            className="w-full py-6 rounded-2xl border-2 border-dashed border-[#EFE3D8] hover:border-[#7B2C3B] hover:bg-[#FBF7F2] flex flex-col items-center justify-center gap-1.5 text-[#6A6A6A] transition-all disabled:opacity-60"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Music2 className="w-5 h-5 text-[#7B2C3B]" />}
            <span className="text-sm font-medium text-[#2A2A2A]">{uploading ? 'Uploading…' : 'Upload a song'}</span>
            <span className="text-[11px]">MP3, M4A, AAC, OGG, WAV · up to 15 MB</span>
          </button>
        )
      )}

      {tab === 'link' && (
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#9A8A7E]">Streaming URL</label>
            <div className="mt-1 flex gap-2">
              <input
                data-testid="website-music-link"
                type="url"
                value={linkDraft}
                onChange={(e) => setLinkDraft(e.target.value)}
                placeholder="https://open.spotify.com/track/… or https://youtu.be/…"
                className="flex-1 px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
              />
              <button
                type="button"
                data-testid="website-music-link-save"
                onClick={saveLink}
                className="btn-primary text-xs px-3 py-2 rounded-lg"
              >
                Save link
              </button>
            </div>
            {linkErr && <p className="mt-1 text-[11px] text-red-700">{linkErr}</p>}
            {isLink && (
              <p className="mt-2 text-[11px] text-emerald-700 inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Linked to {providerLabel(cfg.musicUrl)} — guests will see a mini-player on the site.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="website-music-title-link"
              value={cfg.musicTitle || ''}
              onChange={(e) => setCfg((p) => ({ ...p, musicTitle: e.target.value }))}
              onBlur={(e) => save({ musicTitle: e.target.value })}
              placeholder="Song title (optional)"
              className="px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
            />
            <input
              data-testid="website-music-artist-link"
              value={cfg.musicArtist || ''}
              onChange={(e) => setCfg((p) => ({ ...p, musicArtist: e.target.value }))}
              onBlur={(e) => save({ musicArtist: e.target.value })}
              placeholder="Artist (optional)"
              className="px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
            />
          </div>
          {isLink && (
            <button
              type="button"
              data-testid="website-music-link-remove"
              onClick={clear}
              className="text-xs text-[#B8896C] hover:text-red-600 inline-flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" /> Remove music
            </button>
          )}
          <p className="text-[11px] text-[#9A8A7E]">
            Tip: Spotify and YouTube support <strong>playlists</strong> too — paste a playlist URL to auto-advance through multiple songs.
          </p>
        </div>
      )}

      <input
        id="music-file-input"
        type="file"
        accept="audio/mpeg,audio/mp3,audio/mp4,audio/aac,audio/x-m4a,audio/m4a,audio/ogg,audio/wav,.mp3,.m4a,.aac,.ogg,.wav"
        onChange={onFile}
        style={{ display: 'none' }}
      />
      {err && <p className="mt-2 text-[11px] text-red-700">{err}</p>}
    </section>
  );
}


function ScheduleEditor({ schedule, onChange }) {
  const [rows, setRows] = useState(schedule || []);
  useEffect(() => { setRows(schedule || []); }, [(schedule || []).length]);
  const setCell = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const commit = () => onChange(rows);
  const add = () => { const next = [...rows, { time: '', title: '', where: '' }]; setRows(next); onChange(next); };
  const remove = (i) => { const next = rows.filter((_, idx) => idx !== i); setRows(next); onChange(next); };
  return (
    <section className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
          Day-of schedule
          <InfoTip testId="info-schedule" title="Your wedding-day timeline">
            <p>The rough flow of your day so guests know when to arrive and what to expect. Times can be approximate.</p>
            <p><strong>A typical schedule</strong>:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>3:30 PM — Guests arrive</li>
              <li>4:00 PM — Ceremony</li>
              <li>4:30 PM — Cocktail hour</li>
              <li>6:00 PM — Dinner</li>
              <li>8:00 PM — First dance &amp; dancing</li>
              <li>11:00 PM — Send-off</li>
            </ul>
            <p>Add or remove rows — this appears in a clean, minimal list on your site.</p>
          </InfoTip>
        </h3>
        <div className="flex items-center gap-2">
          <AiAssistButton
            feature="schedule"
            testId="schedule-ai-suggest"
            label="Suggest a timeline"
            onResult={(data) => {
              const items = (data?.items || []).map((it) => ({
                time: it.time || '',
                title: it.title || '',
                where: it.where || '',
              }));
              if (!items.length) return;
              // If the couple already has rows, append; otherwise replace with the AI suggestion.
              const next = rows.length ? [...rows, ...items] : items;
              setRows(next);
              onChange(next);
            }}
          />
          <button onClick={add} data-testid="website-schedule-add" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B]">
            <Plus className="w-3.5 h-3.5" /> Add row
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {(rows).map((row, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <input data-testid={`schedule-time-${i}`} placeholder="3:30 PM" value={row.time || ''} onChange={(e) => setCell(i, { time: e.target.value })} onBlur={commit} className="col-span-3 px-2.5 py-2 rounded-lg border border-[#EFE3D8] text-sm" />
            <input data-testid={`schedule-title-${i}`} placeholder="Ceremony" value={row.title || ''} onChange={(e) => setCell(i, { title: e.target.value })} onBlur={commit} className="col-span-5 px-2.5 py-2 rounded-lg border border-[#EFE3D8] text-sm" />
            <input data-testid={`schedule-where-${i}`} placeholder="Where" value={row.where || ''} onChange={(e) => setCell(i, { where: e.target.value })} onBlur={commit} className="col-span-3 px-2.5 py-2 rounded-lg border border-[#EFE3D8] text-sm" />
            <button onClick={() => remove(i)} className="col-span-1 text-[#9A8A7E] hover:text-[#7B2C3B] flex justify-center" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </section>
  );
}

function RegistryEditor({ links, onChange }) {
  const [rows, setRows] = useState(links || []);
  useEffect(() => { setRows(links || []); }, [(links || []).length]);
  const setCell = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const commit = () => onChange(rows);
  const add = (type = 'link') => { const next = [...rows, { label: '', url: '', type, goal: '' }]; setRows(next); onChange(next); };
  const remove = (i) => { const next = rows.filter((_, idx) => idx !== i); setRows(next); onChange(next); };
  return (
    <section className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
          Registry &amp; cash funds
          <InfoTip testId="info-registry" title="Where guests can send gifts">
            <p>Add links to any registries or cash funds — guests will see them as tidy cards on your site.</p>
            <p><strong>External link</strong>: paste the URL to your registry at Amazon, Zola, Crate &amp; Barrel, Target, Bloomingdale&apos;s, etc.</p>
            <p><strong>Cash fund</strong>: point to your Venmo / PayPal / CashApp / Stripe link. Give it a purpose (e.g. "Honeymoon in Kyoto") and an optional goal amount.</p>
            <p>We never charge a referral fee and never sell your guests&apos; data.</p>
          </InfoTip>
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={() => add('link')} data-testid="website-registry-add" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B]">
            <Plus className="w-3.5 h-3.5" /> Add link
          </button>
          <button onClick={() => add('cash')} data-testid="website-cashfund-add" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B]">
            <Plus className="w-3.5 h-3.5" /> Add cash fund
          </button>
        </div>
      </div>
      {rows.length === 0 && (
        <p className="text-xs text-[#9A8A7E]">
          No registries yet. Paste links to <em>any</em> external registry, or add a cash fund pointing to your Venmo / PayPal / Stripe link.
          We don&apos;t charge a referral fee — and we never sell your guests&apos; data.
        </p>
      )}
      <div className="space-y-2">
        {rows.map((row, i) => {
          const isCash = row.type === 'cash';
          return (
            <div key={i} className="p-3 rounded-xl border border-[#EFE3D8] bg-[#FBF7F2]/50">
              <div className="flex items-center justify-between mb-2">
                <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${isCash ? 'bg-[#F4D6CE] text-[#7B2C3B]' : 'bg-[#EAF2E5] text-[#4A7A4A]'}`}>
                  {isCash ? 'Cash fund' : 'External link'}
                </span>
                <button onClick={() => remove(i)} className="text-[#9A8A7E] hover:text-[#7B2C3B] p-1" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-12 gap-2 items-center">
                <input
                  data-testid={`registry-label-${i}`}
                  placeholder={isCash ? 'Honeymoon Fund' : 'Bloomingdale\u2019s'}
                  value={row.label || ''}
                  onChange={(e) => setCell(i, { label: e.target.value })}
                  onBlur={commit}
                  className="col-span-5 px-2.5 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white"
                />
                <input
                  data-testid={`registry-url-${i}`}
                  placeholder={isCash ? 'https://venmo.com/yourhandle' : 'https://…'}
                  value={row.url || ''}
                  onChange={(e) => setCell(i, { url: e.target.value })}
                  onBlur={commit}
                  className={`${isCash ? 'col-span-4' : 'col-span-7'} px-2.5 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white`}
                />
                {isCash && (
                  <input
                    data-testid={`registry-goal-${i}`}
                    placeholder="Goal ($)"
                    value={row.goal || ''}
                    onChange={(e) => setCell(i, { goal: e.target.value.replace(/[^0-9.]/g, '') })}
                    onBlur={commit}
                    className="col-span-3 px-2.5 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FaqEditor({ faq, onChange }) {
  const [rows, setRows] = useState(faq || []);
  useEffect(() => { setRows(faq || []); }, [(faq || []).length]);
  const setCell = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const commit = () => onChange(rows);
  const add = () => { const next = [...rows, { q: '', a: '' }]; setRows(next); onChange(next); };
  const remove = (i) => { const next = rows.filter((_, idx) => idx !== i); setRows(next); onChange(next); };
  return (
    <section className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
          FAQs
          <InfoTip testId="info-faq" title="Answer the questions guests will ask">
            <p>Short Q&amp;A pairs that appear as an accordion on your site. Great for cutting down on repetitive texts.</p>
            <p><strong>Common questions to answer</strong>:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>What should I wear? (dress code)</li>
              <li>Can I bring a plus one?</li>
              <li>Are kids invited?</li>
              <li>Is the ceremony indoors or outdoors?</li>
              <li>Where should we park?</li>
              <li>Will there be a shuttle?</li>
              <li>Is the venue accessible?</li>
              <li>Any COVID / health precautions?</li>
            </ul>
          </InfoTip>
        </h3>
        <div className="flex items-center gap-2">
          <AiAssistButton
            feature="faq"
            testId="faq-ai-suggest"
            label="Suggest FAQs"
            context={{ existing: rows.map((r) => r.q).filter(Boolean) }}
            onResult={(data) => {
              const suggested = (data?.items || []).filter((it) => it.q && it.a);
              if (!suggested.length) return;
              // Merge: keep existing rows, append new ones whose question isn't already there
              const existingQs = new Set(rows.map((r) => (r.q || '').trim().toLowerCase()));
              const additions = suggested.filter((it) => !existingQs.has(it.q.trim().toLowerCase()));
              const next = [...rows, ...additions];
              setRows(next);
              onChange(next);
            }}
          />
          <button onClick={add} data-testid="website-faq-add" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B]">
            <Plus className="w-3.5 h-3.5" /> Add Q&amp;A
          </button>
        </div>
      </div>
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="p-3 rounded-xl border border-[#EFE3D8]">
            <div className="flex items-start gap-2">
              <input data-testid={`faq-q-${i}`} placeholder="Question" value={row.q || ''} onChange={(e) => setCell(i, { q: e.target.value })} onBlur={commit} className="flex-1 px-2.5 py-2 rounded-lg border border-[#EFE3D8] text-sm" />
              <button onClick={() => remove(i)} className="text-[#9A8A7E] hover:text-[#7B2C3B] p-2" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
            </div>
            <textarea data-testid={`faq-a-${i}`} rows={2} placeholder="Answer" value={row.a || ''} onChange={(e) => setCell(i, { a: e.target.value })} onBlur={commit} className="mt-2 w-full px-2.5 py-2 rounded-lg border border-[#EFE3D8] text-sm" />
          </div>
        ))}
      </div>
    </section>
  );
}

function MiniPreview({ cfg }) {
  const wash = cfg.wash || '#F4E4DA';
  const accent = cfg.accent || '#7B2C3B';
  return (
    <div className="text-[10px] leading-tight" style={{ background: wash }}>
      <div className="px-4 py-6 text-center" style={{ color: accent }}>
        {cfg.heroPhoto && (
          <img src={cfg.heroPhoto} alt="" className="w-full h-24 object-cover rounded-lg mb-3 opacity-90" />
        )}
        <Heart className="w-4 h-4 mx-auto mb-1" style={{ color: accent, fill: accent }} />
        <p className="font-serif text-base">{cfg.coverHeadline || 'Your names'}</p>
        <p className="opacity-70 mt-0.5 text-[9px]">{cfg.weddingDate || 'A beautiful day soon'}</p>
        <p className="opacity-70 text-[9px]">{cfg.location || ''}</p>
      </div>
      <div className="bg-white px-4 py-3 space-y-2 text-[#2A2A2A]">
        {cfg.sections?.story && (<div><p className="font-serif text-xs" style={{ color: accent }}>Our story</p><p className="opacity-75 line-clamp-2">{cfg.storyText || '…'}</p></div>)}
        {cfg.sections?.schedule && (<div><p className="font-serif text-xs" style={{ color: accent }}>Schedule</p>{(cfg.schedule || []).slice(0, 3).map((r, i) => <p key={i} className="opacity-75">{r.time} · {r.title}</p>)}</div>)}
        {cfg.sections?.registry && (<div><p className="font-serif text-xs" style={{ color: accent }}>Registry</p><p className="opacity-75">{(cfg.registryLinks || []).length} link{(cfg.registryLinks || []).length === 1 ? '' : 's'}</p></div>)}
        {cfg.sections?.faq && (<div><p className="font-serif text-xs" style={{ color: accent }}>FAQs</p><p className="opacity-75">{(cfg.faq || []).length} questions</p></div>)}
        {cfg.sections?.rsvp && (<div className="text-center mt-2"><span className="inline-block px-3 py-1 rounded-full text-white text-[10px]" style={{ background: accent }}>RSVP</span></div>)}
      </div>
    </div>
  );
}
